/**
 * What a FINISHED exercise shows while the compact-feedback switch is on (#4142 follow-up,
 * design-docs/workout-completion-summary.md, boards "Finished exercise — folded" and "— unfolded").
 *
 * Pure and Angular-free, so each rule is unit-tested away from the page. The rules and the reasons
 * behind them: `design-docs/workout-completion-summary.md`. Display only: nothing here decides or
 * writes anything about progression.
 */
import { WEIGHT_GRID_TOLERANCE_KG } from '@cf-shared/data-model/equipment';
import { SetProgressionResult } from './set-progress-display';

/** Whether set 1 did more, the same or fewer reps than last time, at the same weight. */
export enum RepChangeDirection {
  /** green: more reps than last time */
  Up = 'up',
  /** orange: the same reps as last time (#4142 review fixes) */
  Same = 'same',
  /** red: fewer reps than last time (#4142 review fixes; amber before) */
  Down = 'down',
}

/** The "+N reps vs last time" / "Same as last time" / "−N reps vs last time" chip. */
export interface RepChangeChip {
  direction: RepChangeDirection;
  /** how many reps more or fewer, always positive; 0 for the same */
  reps: number;
}

/**
 * The chip comparing set 1 with last time. Shown only when both were done at the same weight (within
 * the equipment grid's tolerance, because lb values are stored as converted kg).
 * "Last time" is the backup's `lastStats`, the set the engine recorded as the one to beat.
 */
export function getRepChangeChip(setOne: SetProgressionResult | undefined): RepChangeChip | undefined {
  const last = setOne?.lastTime;
  if (!setOne || !last) {
    return undefined;
  }
  // a different weight makes a rep count meaningless to compare (owner decision 2026-10-09)
  if (Math.abs(setOne.done.weight_kg - last.weight_kg) > WEIGHT_GRID_TOLERANCE_KG) {
    return undefined;
  }
  const change = setOne.done.reps - last.reps;
  // equal reps get the orange chip (#4142 review fixes; before, no chip, owner decision 2026-10-09)
  if (change === 0) {
    return { direction: RepChangeDirection.Same, reps: 0 };
  }
  return change > 0
    ? { direction: RepChangeDirection.Up, reps: change }
    : { direction: RepChangeDirection.Down, reps: -change };
}

/** The live plan-path fields the "Next" rule reads. Loosely typed for old or damaged documents. */
export interface NextTargetPathFields {
  min_reps?: unknown;
  target_reps?: unknown;
}

/**
 * The "Next: N+ reps" number: the floor the engine will test next session, or its rep target when
 * there is no range. Only for today's workout, because a past workout's live plan path has moved on
 * since. Undefined when nothing usable is stored.
 */
export function getNextRepTarget(isCurrentWorkout: boolean, path: NextTargetPathFields | null | undefined): number | undefined {
  if (!isCurrentWorkout || !path) {
    return undefined;
  }
  const target = finiteOrUndefined(path.target_reps);
  const floor = finiteOrUndefined(path.min_reps);
  // a floor of 1 means the weight just went up, and a floor at or above the target is not a range
  if (floor !== undefined && target !== undefined && floor > 1 && floor < target) {
    return floor;
  }
  return target;
}

export interface FinishedExerciseSummaryInput {
  /** today's sets as one string, without the planned-weight brackets; empty when none were done */
  setsText: string;
  /** every set of the exercise was skipped */
  allSkipped: boolean;
  /** set 1's progression result, when it has one */
  setOne: SetProgressionResult | undefined;
  /** the exercise has a benchmark (`Regular1st`) set today; without one, next session's set 1 is not a range */
  hasBenchmarkSet: boolean;
  /** last workout's sets as one string, when loaded */
  lastWorkoutSetsText: string | undefined;
  /** the page's `isEditableMode()`: today's workout or a soft-future one */
  isEditableMode: boolean;
  /** the workout is today's (`WorkoutLoadMode.Current`) */
  isCurrentWorkout: boolean;
  /** the exercise's live plan path */
  planPath: NextTargetPathFields | null | undefined;
}

/** What a finished exercise's folded card shows under its name. */
export interface FinishedExerciseSummary {
  /** "Skipped" and nothing else */
  skipped: boolean;
  /** today's sets, e.g. "120 kg × 12, 11, 10" */
  setsText?: string;
  chip?: RepChangeChip;
  /** "Last: <last workout's sets>" */
  lastWorkoutSetsText?: string;
  /** "Next: N+ reps" */
  nextReps?: number;
}

/**
 * Builds a finished exercise's folded summary. "Last" shows only where the page's own "Last Workout"
 * row shows (`isEditableMode()`): its loader skips only the open workout, so on a past workout it
 * would find a LATER one. "Next" shows only in today's workout, and only for an exercise whose set 1
 * today was a benchmark set.
 */
export function getFinishedExerciseSummary(input: FinishedExerciseSummaryInput): FinishedExerciseSummary {
  if (input.allSkipped) {
    return { skipped: true };
  }
  const summary: FinishedExerciseSummary = { skipped: false };
  if (input.setsText) {
    summary.setsText = input.setsText;
  }
  const chip = getRepChangeChip(input.setOne);
  if (chip) {
    summary.chip = chip;
  }
  if (input.isEditableMode && input.lastWorkoutSetsText) {
    summary.lastWorkoutSetsText = input.lastWorkoutSetsText;
  }
  // a strength or rep test today says nothing about next session's set 1, so it gets no "Next"
  const nextReps = input.hasBenchmarkSet ? getNextRepTarget(input.isCurrentWorkout, input.planPath) : undefined;
  if (nextReps !== undefined) {
    summary.nextReps = nextReps;
  }
  return summary;
}

// a value is usable only when it is a finite number
function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
